import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface EmailCredential {
  id: string;
  teamId: string;
  userId: string | null;
  provider: "google_oauth";
  googleEmail: string | null;
  fromName: string | null;
  fromEmail: string | null;
  isVerified: boolean;
  isActive: boolean;
  isTeamLevel: boolean;
  createdAt: string;
  updatedAt: string;
}

interface EmailCredentialsResponse {
  teamCredentials: EmailCredential[];
  userCredentials: EmailCredential[];
  effectiveProvider: string | null;
  effectiveCredential: EmailCredential | null;
  hasCredentials: boolean;
}

export function useEmailCredentials() {
  const [data, setData] = useState<EmailCredentialsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);

  const fetchCredentials = useCallback(async () => {
    // Only show loading spinner on initial fetch, not refetches
    if (!hasFetched.current) {
      setLoading(true);
    }
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setData(null);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-email-credentials`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch email credentials");
      }

      const result = await response.json();
      if (result.error) {
        throw new Error(result.error);
      }

      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
      setData(null);
    } finally {
      setLoading(false);
      hasFetched.current = true;
    }
  }, []);

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  return {
    hasCredentials: data?.hasCredentials ?? false,
    effectiveProvider: data?.effectiveProvider ?? null,
    effectiveCredential: data?.effectiveCredential ?? null,
    teamCredentials: data?.teamCredentials ?? [],
    userCredentials: data?.userCredentials ?? [],
    loading,
    error,
    refetch: fetchCredentials,
  };
}
